import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const ROWS_PER_PAGE = 20;

const clampPageIndex = (page, total) => {
  if (total <= 0) {
    return 0;
  }
  const maxPage = Math.max(0, Math.ceil(total / ROWS_PER_PAGE) - 1);
  return Math.min(page, maxPage);
};

const decodeDate = (value) => {
  if (!value) {
    return null;
  }

  // If value is a number, determine if it's epoch seconds or milliseconds
  // JavaScript Date treats numbers as milliseconds
  if (typeof value === 'number') {
    // Range for years 2000-2100 in milliseconds
    const MIN_MS = 946684800000;  // 2000-01-01
    const MAX_MS = 4102444800000; // 2100-01-01
    
    // If value is in the millisecond range for modern dates, use directly
    if (value >= MIN_MS && value <= MAX_MS) {
      return new Date(value);
    }
    
    // Otherwise assume it's epoch seconds and convert to milliseconds
    if (value > 0 && value < MIN_MS) {
      return new Date(value * 1000);
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const formatDateTime = (value, fallback) => {
  const date = decodeDate(value);
  if (!date) {
    return fallback;
  }
  return date.toLocaleString();
};

const formatFileSize = (bytes) => {
  if (typeof bytes !== "number" || Number.isNaN(bytes)) {
    return "";
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
};

const defaultLdapSettingsState = {
  enabled: false,
  url: "",
  base_dn: "",
  bind_dn: "",
  bind_password: "",
  bind_password_set: false,
  login_attribute: "mail",
  email_attribute: "mail",
  name_attribute: "cn",
  default_role: "user",
  tls_insecure: false,
  clear_bind_password: false,
};

const mapSystemSettingsResponse = (payload = {}) => {
  const ldapPayload = payload.ldap || {};
  return {
    app_base_url: payload.app_base_url || "",
    require_email_verification:
      typeof payload.require_email_verification === "boolean"
        ? payload.require_email_verification
        : true,
    smtp_configured: Boolean(payload.smtp_configured),
    ldap: {
      ...defaultLdapSettingsState,
      enabled: Boolean(ldapPayload.enabled),
      url: ldapPayload.url || "",
      base_dn: ldapPayload.base_dn || "",
      bind_dn: ldapPayload.bind_dn || "",
      login_attribute: ldapPayload.login_attribute || "mail",
      email_attribute: ldapPayload.email_attribute || "mail",
      name_attribute: ldapPayload.name_attribute || "cn",
      default_role: ldapPayload.default_role || "user",
      tls_insecure: Boolean(ldapPayload.tls_insecure),
      bind_password_set: Boolean(ldapPayload.bind_password_set),
      bind_password: "",
      clear_bind_password: false,
    },
  };
};

const AdminDeviceDashboard = ({
  token,
  serverUrl,
  showMessage,
  userRole = "admin",
  ldapEnabled = false,
}) => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [selectedDeviceDetail, setSelectedDeviceDetail] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");
  const [linkingUser, setLinkingUser] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    role: "user",
  });
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);
  const [creatingDeviceType, setCreatingDeviceType] = useState(false);
  const [newDeviceTypeName, setNewDeviceTypeName] = useState("");
  const [deviceTypeDraft, setDeviceTypeDraft] = useState("");
  const [updatingDeviceType, setUpdatingDeviceType] = useState(false);
  const [firmwareReleases, setFirmwareReleases] = useState([]);
  const [loadingFirmwareReleases, setLoadingFirmwareReleases] = useState(false);
  const [firmwareFilterType, setFirmwareFilterType] = useState("");
  const [firmwareUpload, setFirmwareUpload] = useState({
    deviceTypeId: "",
    version: "",
    notes: "",
  });
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [uploadingFirmware, setUploadingFirmware] = useState(false);
  const [selectedFirmwareReleaseId, setSelectedFirmwareReleaseId] = useState("");
  const [sendingFirmwareUpdate, setSendingFirmwareUpdate] = useState(false);
  const [deletingFirmwareReleaseId, setDeletingFirmwareReleaseId] = useState(null);
  const [wifiConfigDraft, setWifiConfigDraft] = useState({
    ssid: "",
    password: "",
  });
  const [savingWifiConfig, setSavingWifiConfig] = useState(false);
  const [cloudConfigDraft, setCloudConfigDraft] = useState({
    cloud_device_id: "",
    cloud_token: "",
    key_length: "6",
  });
  const [savingCloudConfig, setSavingCloudConfig] = useState(false);
  const [codesPage, setCodesPage] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [eventsPage, setEventsPage] = useState(0);
  const [mainTab, setMainTab] = useState("devices");
  const [deviceDetailTab, setDeviceDetailTab] = useState("overview");
  const [manufacturingForm, setManufacturingForm] = useState({
    deviceName: "",
    deviceTypeId: "",
    serial: "",
    wifiSsid: "",
    wifiPassword: "",
    keyLength: "6",
    environment: "esp32dev",
  });
  const [manufacturingResult, setManufacturingResult] = useState(null);
  const [manufacturingLoading, setManufacturingLoading] = useState(false);
  const [manufacturingPattern, setManufacturingPattern] = useState("");
  const [manufacturingMatches, setManufacturingMatches] = useState([]);
  const [loadingManufacturingMatches, setLoadingManufacturingMatches] =
    useState(false);
  const [sendingManufacturingUpdate, setSendingManufacturingUpdate] =
    useState(false);
  const [blockingDevice, setBlockingDevice] = useState(false);
  const [clearingLockout, setClearingLockout] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState(false);
  const [systemSettings, setSystemSettings] = useState(() =>
    mapSystemSettingsResponse()
  );
  const [loadingSystemSettings, setLoadingSystemSettings] = useState(false);
  const [savingSystemSettings, setSavingSystemSettings] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingUserDraft, setEditingUserDraft] = useState({
    username: "",
    email: "",
    role: "user",
    password: "",
  });
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  const [ldapAssignmentIdentifier, setLdapAssignmentIdentifier] = useState("");
  const [importingLdapUser, setImportingLdapUser] = useState(false);
  const canManageDevices = userRole === "admin" || userRole === "manager";
  const canManageSystem = userRole === "admin";
  const availableTabs = useMemo(() => {
    const tabs = ["devices"];
    if (canManageDevices) {
      tabs.push("firmware");
    }
    if (canManageSystem) {
      tabs.push("users");
      tabs.push("system");
    }
    return tabs;
  }, [canManageDevices, canManageSystem]);
  const ldapActive =
    Boolean(systemSettings?.ldap?.enabled) || Boolean(ldapEnabled);

  useEffect(() => {
    if (!availableTabs.includes(mainTab)) {
      setMainTab(availableTabs[0] || "devices");
    }
  }, [availableTabs, mainTab]);

  useEffect(() => {
    setCodesPage(0);
    setHistoryPage(0);
    setEventsPage(0);
    setDeviceDetailTab("overview");
    setAssignUserId("");
    setLdapAssignmentIdentifier("");
    setManufacturingPattern(
      selectedDeviceId ? `device-${selectedDeviceId}` : ""
    );
    setManufacturingMatches([]);
  }, [selectedDeviceId]);

  useEffect(() => {
    const keysCount = selectedDeviceDetail?.keys?.length ?? 0;
    const historyCount = selectedDeviceDetail?.history?.length ?? 0;
    const eventsCount = selectedDeviceDetail?.state_events?.length ?? 0;
    setCodesPage((prev) => clampPageIndex(prev, keysCount));
    setHistoryPage((prev) => clampPageIndex(prev, historyCount));
    setEventsPage((prev) => clampPageIndex(prev, eventsCount));
  }, [selectedDeviceDetail]);

  const fetchDevices = useCallback(() => {
    if (!token) {
      return;
    }

    setLoadingDevices(true);
    axios
      .get(`${serverUrl}/admin/devices`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        const fetched = res.data?.devices || [];
        setDevices(fetched);
        setSelectedDeviceId((current) => {
          if (current && fetched.some((device) => device.id === current)) {
            return current;
          }
          return fetched.length ? fetched[0].id : null;
        });
        if (!fetched.length) {
          setSelectedDeviceDetail(null);
        }
      })
      .catch((err) => {
        console.error("Failed to load admin devices", err);
        showMessage(t("adminLoadDevicesFailed"));
        setDevices([]);
        setSelectedDeviceId(null);
        setSelectedDeviceDetail(null);
      })
      .finally(() => setLoadingDevices(false));
  }, [serverUrl, showMessage, t, token]);

  const fetchDeviceDetail = useCallback(
    (deviceId) => {
      if (!deviceId || !token) {
        return;
      }

      setLoadingDetail(true);
      axios
        .get(`${serverUrl}/admin/device/${deviceId}`, {
          headers: { Authorization: token },
        })
        .then((res) => {
          setSelectedDeviceDetail(res.data);
        })
        .catch((err) => {
          console.error("Failed to load admin device detail", err);
          showMessage(t("adminLoadDeviceFailed"));
          setSelectedDeviceDetail(null);
        })
        .finally(() => setLoadingDetail(false));
    },
    [serverUrl, showMessage, t, token]
  );

  const fetchUsers = useCallback(() => {
    if (!token) {
      return;
    }

    setLoadingUsers(true);
    axios
      .get(`${serverUrl}/admin/users`, { headers: { Authorization: token } })
      .then((res) => {
        const normalized =
          res.data?.users?.map((user) => {
            const provider = (user.auth_provider || "local").toLowerCase();
            return {
              ...user,
              auth_provider: provider,
              is_ldap: Boolean(user.is_ldap || provider === "ldap"),
            };
          }) || [];
        setUsers(normalized);
      })
      .catch((err) => {
        console.error("Failed to load admin users", err);
        showMessage(t("adminLoadUsersFailed"));
        setUsers([]);
      })
      .finally(() => setLoadingUsers(false));
  }, [serverUrl, showMessage, t, token]);

  const fetchDeviceTypes = useCallback(() => {
    if (!token) {
      return;
    }

    setLoadingDeviceTypes(true);
    axios
      .get(`${serverUrl}/admin/device-types`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        setDeviceTypes(res.data?.device_types || []);
      })
      .catch((err) => {
        console.error("Failed to load device types", err);
        showMessage(t("adminLoadDeviceTypesFailed"));
        setDeviceTypes([]);
      })
      .finally(() => setLoadingDeviceTypes(false));
  }, [serverUrl, showMessage, t, token]);

  const fetchFirmwareReleases = useCallback(() => {
    if (!token) {
      return;
    }

    setLoadingFirmwareReleases(true);
    axios
      .get(`${serverUrl}/admin/firmware/releases`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        setFirmwareReleases(res.data?.firmware_releases || []);
      })
      .catch((err) => {
        console.error("Failed to load firmware releases", err);
        showMessage(t("adminLoadFirmwareFailed"));
        setFirmwareReleases([]);
      })
      .finally(() => setLoadingFirmwareReleases(false));
  }, [serverUrl, showMessage, t, token]);

  const fetchSystemSettings = useCallback(() => {
    if (!token || !canManageSystem) {
      return;
    }
    setLoadingSystemSettings(true);
    axios
      .get(`${serverUrl}/admin/settings`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        const payload = res.data?.settings || {};
        setSystemSettings(mapSystemSettingsResponse(payload));
      })
      .catch((error) => {
        console.error("Failed to load system settings", error);
        showMessage(t("adminSettingsLoadFailed"));
      })
      .finally(() => setLoadingSystemSettings(false));
  }, [canManageSystem, serverUrl, showMessage, t, token]);

  useEffect(() => {
    if (token) {
      fetchDevices();
      fetchUsers();
      fetchDeviceTypes();
      if (canManageDevices) {
        fetchFirmwareReleases();
      }
      if (canManageSystem) {
        fetchSystemSettings();
      }
    } else {
      setDevices([]);
      setSelectedDeviceId(null);
      setSelectedDeviceDetail(null);
      setUsers([]);
      setDeviceTypes([]);
      setFirmwareReleases([]);
    }
  }, [
    canManageDevices,
    canManageSystem,
    fetchDeviceTypes,
    fetchDevices,
    fetchFirmwareReleases,
    fetchSystemSettings,
    fetchUsers,
    token,
  ]);

  useEffect(() => {
    if (selectedDeviceId) {
      setSelectedDeviceDetail(null);
      fetchDeviceDetail(selectedDeviceId);
    } else {
      setSelectedDeviceDetail(null);
    }
  }, [fetchDeviceDetail, selectedDeviceId]);

  useEffect(() => {
    if (selectedDeviceDetail?.device) {
      const typeId = selectedDeviceDetail.device.device_type_id;
      setDeviceTypeDraft(typeId ? String(typeId) : "");
    } else {
      setDeviceTypeDraft("");
    }
    setSelectedFirmwareReleaseId("");
  }, [selectedDeviceDetail]);

  useEffect(() => {
    if (!firmwareUpload.deviceTypeId && deviceTypes.length) {
      setFirmwareUpload((prev) => ({
        ...prev,
        deviceTypeId: String(deviceTypes[0].id),
      }));
    }
  }, [deviceTypes, firmwareUpload.deviceTypeId]);

  useEffect(() => {
    if (selectedDeviceDetail?.wifi_config) {
      setWifiConfigDraft({
        ssid: selectedDeviceDetail.wifi_config.ssid || "",
        password: selectedDeviceDetail.wifi_config.password || "",
      });
    } else {
      setWifiConfigDraft({ ssid: "", password: "" });
    }

    if (selectedDeviceDetail?.cloud_config) {
      const keyLen = selectedDeviceDetail.cloud_config.key_length || 6;
      setCloudConfigDraft({
        cloud_device_id:
          selectedDeviceDetail.cloud_config.cloud_device_id || "",
        cloud_token: selectedDeviceDetail.cloud_config.cloud_token || "",
        key_length: String(keyLen),
      });
    } else {
      setCloudConfigDraft({ cloud_device_id: "", cloud_token: "", key_length: "6" });
    }
  }, [selectedDeviceDetail]);

  const handleSelectDevice = (deviceId) => {
    setSelectedDeviceId(deviceId);
  };

  const handleCreateUser = () => {
    if (!newUser.username || !newUser.email || !newUser.password) {
      showMessage(t("adminCreateUserRequired"));
      return;
    }

    setCreatingUser(true);
    axios
      .post(
        `${serverUrl}/admin/users`,
        {
          username: newUser.username,
          email: newUser.email,
          password: newUser.password,
          role: newUser.role,
        },
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminCreateUserSuccess"));
        setNewUser({ username: "", email: "", password: "", role: "user" });
        fetchUsers();
      })
      .catch((err) => {
        console.error("Failed to create admin user", err);
        showMessage(t("adminCreateUserFailed"));
      })
      .finally(() => setCreatingUser(false));
  };

  const handleOpenEditUser = (user) => {
    if (!user) {
      return;
    }
    setEditingUser(user);
    setEditingUserDraft({
      username: user.username || "",
      email: user.email || "",
      role: user.role || "user",
      password: "",
    });
  };

  const handleCloseEditUser = () => {
    if (savingUserEdit) {
      return;
    }
    setEditingUser(null);
    setEditingUserDraft({ username: "", email: "", role: "user", password: "" });
  };

  const handleSaveEditedUser = () => {
    if (!editingUser) {
      return;
    }
    const userIsLdap = Boolean(editingUser.is_ldap);
    const payload = {};

    if (userIsLdap) {
      if (!editingUserDraft.role || editingUserDraft.role === editingUser.role) {
        showMessage(t("adminUserEditRoleOnly"));
        return;
      }
      payload.role = editingUserDraft.role;
    } else {
      const trimmedName = editingUserDraft.username.trim();
      const trimmedEmail = editingUserDraft.email.trim();
      if (!trimmedName) {
        showMessage(t("adminUserEditLocalNameRequired"));
        return;
      }
      if (!trimmedEmail) {
        showMessage(t("adminUserEditLocalEmailRequired"));
        return;
      }
      payload.username = trimmedName;
      payload.email = trimmedEmail;
      payload.role = editingUserDraft.role || editingUser.role || "user";
      if (editingUserDraft.password) {
        if (editingUserDraft.password.length < 6) {
          showMessage(t("adminUserEditPasswordLength"));
          return;
        }
        payload.password = editingUserDraft.password;
      }
    }

    setSavingUserEdit(true);
    axios
      .patch(
        `${serverUrl}/admin/users/${editingUser.id}`,
        payload,
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminUserEditSuccess"));
        handleCloseEditUser();
        fetchUsers();
      })
      .catch((error) => {
        console.error("Failed to update user", error);
        const message =
          error?.response?.data?.message || t("adminUserEditFailed");
        showMessage(message);
      })
      .finally(() => setSavingUserEdit(false));
  };

  const handleCreateDeviceType = () => {
    const name = newDeviceTypeName.trim();
    if (!name) {
      showMessage(t("adminDeviceTypeNameRequired"));
      return;
    }

    setCreatingDeviceType(true);
    axios
      .post(
        `${serverUrl}/admin/device-types`,
        { name },
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminDeviceTypeCreateSuccess"));
        setNewDeviceTypeName("");
        fetchDeviceTypes();
      })
      .catch((err) => {
        console.error("Failed to create device type", err);
        showMessage(t("adminDeviceTypeCreateFailed"));
      })
      .finally(() => setCreatingDeviceType(false));
  };

  const handleUpdateDeviceType = () => {
    if (!selectedDeviceId) {
      return;
    }

    const payload =
      deviceTypeDraft === "" ? { device_type_id: null } : { device_type_id: Number(deviceTypeDraft) };

    setUpdatingDeviceType(true);
    axios
      .patch(
        `${serverUrl}/admin/device/${selectedDeviceId}`,
        payload,
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminDeviceTypeUpdateSuccess"));
        fetchDevices();
        fetchDeviceDetail(selectedDeviceId);
      })
      .catch((err) => {
        console.error("Failed to update device type", err);
        showMessage(t("adminDeviceTypeUpdateFailed"));
      })
      .finally(() => setUpdatingDeviceType(false));
  };

  const handleFirmwareFileChange = (event) => {
    const file = event?.target?.files?.[0] || null;
    setFirmwareFile(file);
  };

  const handleFirmwareUpload = () => {
    if (!firmwareUpload.deviceTypeId) {
      showMessage(t("adminFirmwareDeviceTypeRequired"));
      return;
    }
    if (!firmwareUpload.version.trim()) {
      showMessage(t("adminFirmwareVersionRequired"));
      return;
    }
    if (!firmwareFile) {
      showMessage(t("adminFirmwareFileRequired"));
      return;
    }

    const formData = new FormData();
    formData.append("device_type_id", firmwareUpload.deviceTypeId);
    formData.append("version", firmwareUpload.version.trim());
    if (firmwareUpload.notes.trim()) {
      formData.append("notes", firmwareUpload.notes.trim());
    }
    formData.append("file", firmwareFile);

    setUploadingFirmware(true);
    axios
      .post(`${serverUrl}/admin/firmware/releases`, formData, {
        headers: {
          Authorization: token,
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        showMessage(t("adminFirmwareUploadSuccess"));
        setFirmwareUpload((prev) => ({
          ...prev,
          version: "",
          notes: "",
        }));
        setFirmwareFile(null);
        fetchFirmwareReleases();
      })
      .catch((err) => {
        console.error("Failed to upload firmware", err);
        showMessage(t("adminFirmwareUploadFailed"));
      })
      .finally(() => setUploadingFirmware(false));
  };

  const handleManufacturingInputChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setManufacturingForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleManufactureDevice = () => {
    if (!token) {
      return;
    }
    const trimmedName = manufacturingForm.deviceName.trim();
    if (!trimmedName) {
      showMessage(t("adminManufactureNameRequired"));
      return;
    }

    const payload = {
      device_name: trimmedName,
      device_type_id: manufacturingForm.deviceTypeId
        ? Number(manufacturingForm.deviceTypeId)
        : null,
      serial: manufacturingForm.serial.trim() || null,
      wifi_ssid: manufacturingForm.wifiSsid.trim() || null,
      wifi_password: manufacturingForm.wifiPassword,
      key_length: Number(manufacturingForm.keyLength) || 6,
      environment:
        manufacturingForm.environment.trim() || "esp32dev",
    };

    setManufacturingLoading(true);
    axios
      .post(`${serverUrl}/admin/manufacture`, payload, {
        headers: { Authorization: token },
      })
      .then((res) => {
        const result = res.data?.manufacturing || null;
        setManufacturingResult(result);
        showMessage(t("adminManufactureSuccess"));
        if (result?.device_id) {
          fetchDevices();
        }
      })
      .catch((err) => {
        console.error("Failed to create manufacturing build", err);
        const message =
          err?.response?.data?.message || t("adminManufactureFailed");
        showMessage(message);
      })
      .finally(() => setManufacturingLoading(false));
  };

  const extractFilenameFromUrl = (value) => {
    if (!value) {
      return "firmware.bin";
    }
    try {
      const parsed = new URL(value, window.location.origin);
      const relPath = parsed.searchParams.get("path");
      if (relPath) {
        const segments = relPath.split("/");
        return segments[segments.length - 1] || "firmware.bin";
      }
    } catch (error) {
      // ignore
    }
    const segments = value.split("/");
    return segments[segments.length - 1] || "firmware.bin";
  };

  const handleSecureDownload = useCallback(
    (url, fallbackName) => {
      if (!token || !url) {
        return;
      }
      const downloadName = fallbackName || extractFilenameFromUrl(url);
      axios
        .get(url, {
          headers: { Authorization: token },
          responseType: "blob",
        })
        .then((response) => {
          const blobUrl = window.URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = downloadName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch((error) => {
          console.error("Failed to download firmware", error);
          showMessage(t("adminFirmwareDownloadFailed"));
        });
    },
    [showMessage, t, token]
  );

  const handleFetchManufacturingMatches = () => {
    if (!token || !canManageDevices) {
      return;
    }
    setLoadingManufacturingMatches(true);
    axios
      .get(`${serverUrl}/admin/manufacture/builds`, {
        headers: { Authorization: token },
        params: {
          pattern:
            manufacturingPattern.trim() ||
            (selectedDeviceId ? `device-${selectedDeviceId}` : ""),
          limit: 5,
        },
      })
      .then((res) => {
        setManufacturingMatches(res.data?.builds || []);
      })
      .catch((error) => {
        console.error("Failed to list manufacturing builds", error);
        showMessage(t("adminManufactureFailed"));
      })
      .finally(() => setLoadingManufacturingMatches(false));
  };

  const handleSendManufacturingUpdate = () => {
    if (!token || !selectedDeviceId || !canManageDevices) {
      return;
    }
    setSendingManufacturingUpdate(true);
    axios
      .post(
        `${serverUrl}/admin/device/${selectedDeviceId}/manufacture-update`,
        {
          pattern:
            manufacturingPattern.trim() ||
            (selectedDeviceId ? `device-${selectedDeviceId}` : ""),
        },
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminManufactureSendSuccess"));
        handleFetchManufacturingMatches();
      })
      .catch((error) => {
        console.error("Failed to send manufacturing build", error);
        const message =
          error?.response?.data?.message || t("adminManufactureSendFailed");
        showMessage(message);
      })
      .finally(() => setSendingManufacturingUpdate(false));
  };

  const handleToggleDeviceBlocked = (blocked) => {
    if (!token || !selectedDeviceId) {
      return;
    }
    setBlockingDevice(true);
    axios
      .patch(
        `${serverUrl}/admin/device/${selectedDeviceId}/block`,
        { blocked },
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminBlockDeviceSuccess"));
        fetchDevices();
        fetchDeviceDetail(selectedDeviceId);
      })
      .catch((error) => {
        console.error("Failed to update block state", error);
        const message =
          error?.response?.data?.message || t("adminBlockDeviceFailed");
        showMessage(message);
      })
      .finally(() => setBlockingDevice(false));
  };

  const handleClearRetryLockout = () => {
    if (!token || !selectedDeviceId) {
      return;
    }
    setClearingLockout(true);
    axios
      .post(
        `${serverUrl}/admin/device/${selectedDeviceId}/clear-lockout`,
        {},
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminClearLockoutSuccess"));
        fetchDevices();
        fetchDeviceDetail(selectedDeviceId);
      })
      .catch((error) => {
        console.error("Failed to clear retry lockout", error);
        const message =
          error?.response?.data?.message || t("adminClearLockoutFailed");
        showMessage(message);
      })
      .finally(() => setClearingLockout(false));
  };

  const handleDeleteDevice = () => {
    if (!token || !selectedDeviceId) {
      return;
    }
    const confirmed = window.confirm(t("adminDeleteDeviceConfirm"));
    if (!confirmed) {
      return;
    }
    setDeletingDevice(true);
    axios
      .delete(`${serverUrl}/admin/device/${selectedDeviceId}`, {
        headers: { Authorization: token },
      })
      .then(() => {
        showMessage(t("adminDeleteDeviceSuccess"));
        setSelectedDeviceId(null);
        setSelectedDeviceDetail(null);
        fetchDevices();
      })
      .catch((error) => {
        console.error("Failed to delete device", error);
        const message =
          error?.response?.data?.message || t("adminDeleteDeviceFailed");
        showMessage(message);
      })
      .finally(() => setDeletingDevice(false));
  };

  const handleLdapFieldChange = (field, value) => {
    setSystemSettings((prev) => ({
      ...prev,
      ldap: {
        ...prev.ldap,
        [field]: value,
      },
    }));
  };

  const handleClearLdapBindPassword = () => {
    setSystemSettings((prev) => ({
      ...prev,
      ldap: {
        ...prev.ldap,
        bind_password: "",
        bind_password_set: false,
        clear_bind_password: true,
      },
    }));
  };

  const handleSaveSystemSettings = () => {
    if (!token) {
      return;
    }
    const trimmedUrl = systemSettings.app_base_url.trim();
    if (!trimmedUrl) {
      showMessage(t("adminSystemBaseUrlRequired"));
      return;
    }
    if (
      systemSettings.ldap?.enabled &&
      (!systemSettings.ldap.url.trim() || !systemSettings.ldap.base_dn.trim())
    ) {
      showMessage(t("adminSystemLdapValidationError"));
      return;
    }
    setSavingSystemSettings(true);
    const ldapPayload = systemSettings.ldap || {};
    const requestBody = {
      app_base_url: trimmedUrl,
      require_email_verification: systemSettings.require_email_verification,
      ldap: {
        enabled: ldapPayload.enabled,
        url: ldapPayload.url,
        base_dn: ldapPayload.base_dn,
        bind_dn: ldapPayload.bind_dn,
        login_attribute: ldapPayload.login_attribute,
        email_attribute: ldapPayload.email_attribute,
        name_attribute: ldapPayload.name_attribute,
        default_role: ldapPayload.default_role,
        tls_insecure: ldapPayload.tls_insecure,
      },
    };
    if (ldapPayload.bind_password) {
      requestBody.ldap.bind_password = ldapPayload.bind_password;
    }
    if (ldapPayload.clear_bind_password) {
      requestBody.ldap.clear_bind_password = true;
    }
    axios
      .patch(
        `${serverUrl}/admin/settings`,
        requestBody,
        { headers: { Authorization: token } }
      )
      .then((res) => {
        const payload = res.data?.settings || {};
        setSystemSettings(mapSystemSettingsResponse(payload));
        showMessage(t("adminSystemUpdated"));
      })
      .catch((error) => {
        console.error("Failed to update system settings", error);
        const message =
          error?.response?.data?.message || t("adminSettingsUpdateFailed");
        showMessage(message);
      })
      .finally(() => setSavingSystemSettings(false));
  };

  const handleDeleteFirmwareRelease = useCallback(
    (release) => {
      if (!token || !release?.id) {
        return;
      }

      const confirmed = window.confirm(
        t("adminFirmwareDeleteConfirm", {
          version: release.version || t("unknown"),
        })
      );
      if (!confirmed) {
        return;
      }

      const releaseId = Number(release.id);
      setDeletingFirmwareReleaseId(releaseId);

      axios
        .delete(`${serverUrl}/admin/firmware/releases/${releaseId}`, {
          headers: { Authorization: token },
        })
        .then(() => {
          setFirmwareReleases((prev) =>
            prev.filter((item) => item.id !== releaseId)
          );
          setSelectedFirmwareReleaseId((current) =>
            Number(current) === releaseId ? "" : current
          );
          showMessage(t("adminFirmwareDeleteSuccess"));
        })
        .catch((err) => {
          console.error("Failed to delete firmware release", err);
          const message =
            err?.response?.data?.message || t("adminFirmwareDeleteFailed");
          showMessage(message);
        })
        .finally(() => setDeletingFirmwareReleaseId(null));
    },
    [serverUrl, showMessage, t, token]
  );

  const handleSendFirmwareUpdate = () => {
    if (!selectedDeviceId) {
      return;
    }
    if (!selectedFirmwareReleaseId) {
      showMessage(t("adminFirmwareSelectReleaseRequired"));
      return;
    }

    setSendingFirmwareUpdate(true);
    axios
      .post(
        `${serverUrl}/admin/device/${selectedDeviceId}/firmware-update`,
        { firmware_release_id: Number(selectedFirmwareReleaseId) },
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminFirmwareUpdateSuccess"));
      })
      .catch((err) => {
        console.error("Failed to send firmware update command", err);
        showMessage(t("adminFirmwareUpdateFailed"));
      })
      .finally(() => setSendingFirmwareUpdate(false));
  };

  const handleWifiConfigFieldChange = (field) => (event) => {
    const value = event.target.value;
    setWifiConfigDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveWifiConfig = () => {
    if (!selectedDeviceId) {
      return;
    }

    if (!wifiConfigDraft.ssid.trim()) {
      showMessage(t("adminWifiSsidRequired"));
      return;
    }

    setSavingWifiConfig(true);
    axios
      .post(
        `${serverUrl}/admin/device/${selectedDeviceId}/wifi-config`,
        {
          ssid: wifiConfigDraft.ssid.trim(),
          password: wifiConfigDraft.password,
        },
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminWifiConfigSuccess"));
        fetchDeviceDetail(selectedDeviceId);
      })
      .catch((err) => {
        console.error("Failed to update Wi-Fi config", err);
        showMessage(t("adminWifiConfigFailed"));
      })
      .finally(() => setSavingWifiConfig(false));
  };

  const handleCloudConfigFieldChange = (field) => (event) => {
    const value = event.target.value;
    setCloudConfigDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveCloudConfig = () => {
    if (!selectedDeviceId) {
      return;
    }

    if (!cloudConfigDraft.cloud_device_id.trim()) {
      showMessage(t("adminCloudConfigDeviceIdRequired"));
      return;
    }

    if (!cloudConfigDraft.cloud_token.trim()) {
      showMessage(t("adminCloudConfigTokenRequired"));
      return;
    }

    const keyLengthNumber = Number(cloudConfigDraft.key_length);
    if (![4, 6].includes(keyLengthNumber)) {
      showMessage(t("adminCloudKeyLengthInvalid"));
      return;
    }

    setSavingCloudConfig(true);
    axios
      .post(
        `${serverUrl}/admin/device/${selectedDeviceId}/cloud-config`,
        {
          cloud_device_id: cloudConfigDraft.cloud_device_id.trim(),
          cloud_token: cloudConfigDraft.cloud_token.trim(),
          key_length: keyLengthNumber,
        },
        { headers: { Authorization: token } }
      )
      .then((res) => {
        const message = res.data?.message || t("adminCloudConfigSuccess");
        showMessage(message);
        fetchDeviceDetail(selectedDeviceId);
      })
      .catch((err) => {
        console.error("Failed to update cloud config", err);
        const message = err.response?.data?.message;
        showMessage(message || t("adminCloudConfigFailed"));
      })
      .finally(() => setSavingCloudConfig(false));
  };

  const handleLinkUser = () => {
    if (!assignUserId || !selectedDeviceId) {
      return;
    }

    setLinkingUser(true);
    axios
      .post(
        `${serverUrl}/admin/device/${selectedDeviceId}/users`,
        { user_id: assignUserId },
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminAssignUserSuccess"));
        setAssignUserId("");
        fetchDeviceDetail(selectedDeviceId);
        fetchUsers();
      })
      .catch((err) => {
        console.error("Failed to link user to device", err);
        showMessage(t("adminAssignUserFailed"));
      })
      .finally(() => setLinkingUser(false));
  };

  const handleImportLdapForDevice = () => {
    const identifier = ldapAssignmentIdentifier.trim();
    if (!identifier) {
      showMessage(t("adminAssignLdapIdentifierRequired"));
      return;
    }
    if (!selectedDeviceId) {
      showMessage(t("adminAssignSelectDeviceFirst"));
      return;
    }
    setImportingLdapUser(true);
    axios
      .post(
        `${serverUrl}/admin/users/import-ldap`,
        { identifier },
        { headers: { Authorization: token } }
      )
      .then((res) => {
        const user = res.data?.user;
        if (!user?.id) {
          throw new Error("Missing user id");
        }
        setLdapAssignmentIdentifier("");
        fetchUsers();
        return axios.post(
          `${serverUrl}/admin/device/${selectedDeviceId}/users`,
          { user_id: user.id },
          { headers: { Authorization: token } }
        );
      })
      .then(() => {
        showMessage(t("adminAssignUserSuccess"));
        fetchDeviceDetail(selectedDeviceId);
      })
      .catch((error) => {
        console.error("Failed to import/assign LDAP user", error);
        const message =
          error?.response?.data?.message || t("adminAssignLdapFailed");
        showMessage(message);
      })
      .finally(() => setImportingLdapUser(false));
  };

  const handleUnlinkUser = (userId) => {
    if (!selectedDeviceId) {
      return;
    }

    axios
      .delete(
        `${serverUrl}/admin/device/${selectedDeviceId}/users/${userId}`,
        { headers: { Authorization: token } }
      )
      .then(() => {
        showMessage(t("adminUnassignUserSuccess"));
        fetchDeviceDetail(selectedDeviceId);
        fetchUsers();
      })
      .catch((err) => {
        console.error("Failed to unlink user from device", err);
        showMessage(t("adminUnassignUserFailed"));
      });
  };

  const assignedUsers = selectedDeviceDetail?.assigned_users || [];
  const availableUsers = users.filter(
    (user) => !assignedUsers.some((assigned) => assigned.id === user.id)
  );

  const renderDeviceList = () => {
    if (loadingDevices) {
      return (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!devices.length) {
      return (
        <Box p={2}>
          <Typography variant="body2" color="text.secondary">
            {t("adminNoDevices")}
          </Typography>
        </Box>
      );
    }

    return (
      <List dense>
        {devices.map((device) => (
          <React.Fragment key={device.id}>
            <ListItemButton
              selected={device.id === selectedDeviceId}
              onClick={() => handleSelectDevice(device.id)}
            >
              <ListItemText
                primary={device.device_name || t("unknown")}
                secondary={
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {`${t("adminState")}: ${device.state || t("unknown")}${
                        device.serial_number ? ` • ${device.serial_number}` : ""
                      }`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`${t("adminFirmwareCurrent")}: ${
                        device.current_firmware_version || t("unknown")
                      }`}
                    </Typography>
                  </>
                }
                secondaryTypographyProps={{ component: "div" }}
              />
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  color={device.online ? "success" : "default"}
                  label={
                    device.online
                      ? t("adminOnlineLabelOnline")
                      : t("adminOnlineLabelOffline")
                  }
                />
                {device.is_blocked ? (
                  <Chip
                    size="small"
                    color={
                      device.block_reason === "too_many_retries"
                        ? "warning"
                        : "error"
                    }
                    label={
                      device.block_reason === "too_many_retries"
                        ? t("adminBlockedRetryChip")
                        : t("adminBlockedChip")
                    }
                  />
                ) : null}
              </Stack>
            </ListItemButton>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>
    );
  };

  const renderStatus = () => {
    const status = selectedDeviceDetail?.status;
    if (!status) {
      return null;
    }
    const isBlocked = Boolean(selectedDeviceDetail?.device?.is_blocked);
    const blockReason = selectedDeviceDetail?.device?.block_reason || null;
    const isRetryBlocked = isBlocked && blockReason === "too_many_retries";
    const isAdminBlocked = isBlocked && blockReason === "admin";

    const lastUnlockLabel = status.last_unlock
      ? `${formatDateTime(
          status.last_unlock.created_at,
          t("unknown")
        )} • ${status.last_unlock.key || t("unknown")}`
      : t("adminNoLastUnlock");

    return (
      <Stack spacing={1}>
        <Typography variant="body1" fontWeight={600}>
          {selectedDeviceDetail?.device?.device_name || t("unknown")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedDeviceDetail?.device?.serial_number}
        </Typography>
        <Typography variant="body2">
          {t("adminState")}: {status.state || t("unknown")}
        </Typography>
        <Typography variant="body2">
          {t("adminOnline")}:{" "}
          {status.online
            ? t("adminOnlineLabelOnline")
            : t("adminOnlineLabelOffline")}
        </Typography>
        <Typography variant="body2">
          {t("adminConnectedAt")}:{" "}
          {formatDateTime(status.connected_at, t("unknown"))}
        </Typography>
        <Typography variant="body2">
          {t("adminLastSeen")}: {formatDateTime(status.last_seen, t("unknown"))}
        </Typography>
        <Typography variant="body2">
          {t("adminLastUnlock")}: {lastUnlockLabel}
        </Typography>
        <Typography variant="body2">
          {t("adminDeviceType")}:{" "}
          {selectedDeviceDetail?.device?.device_type_name ||
            t("adminDeviceTypeUnset")}
        </Typography>
        <Typography variant="body2">
          {t("adminFirmwareCurrent")}:{" "}
          {status.firmware_version || t("unknown")}
        </Typography>
        <Typography variant="body2">
          {t("adminFirmwareRecordedAt")}:{" "}
          {formatDateTime(status.firmware_recorded_at, t("unknown"))}
        </Typography>
        <Typography variant="body2">
          {t("adminFirmwareReportedAt")}:{" "}
          {formatDateTime(status.firmware_reported_at, t("unknown"))}
        </Typography>
        <Typography variant="body2">
          {t("adminKeyLength")}:{" "}
          {selectedDeviceDetail?.cloud_config?.key_length ?? 6}
        </Typography>
        {isBlocked && (
          <Chip
            label={
              isRetryBlocked
                ? t("adminBlockedRetryChip")
                : t("adminBlockedChip")
            }
            color={isRetryBlocked ? "warning" : "error"}
            size="small"
            sx={{ alignSelf: "flex-start" }}
          />
        )}
        {canManageSystem && (
          <Stack
            spacing={1}
            direction={{ xs: "column", sm: "row" }}
            mt={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="outlined"
              color={isAdminBlocked ? "success" : "warning"}
              onClick={() => handleToggleDeviceBlocked(!isAdminBlocked)}
              disabled={blockingDevice || isRetryBlocked}
            >
              {isAdminBlocked ? t("adminUnblockDevice") : t("adminBlockDevice")}
            </Button>
            {isRetryBlocked && (
              <Button
                variant="contained"
                color="warning"
                onClick={handleClearRetryLockout}
                disabled={clearingLockout}
              >
                {t("adminClearRetryLockout")}
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteDevice}
              disabled={deletingDevice}
            >
              {t("adminDeleteDevice")}
            </Button>
          </Stack>
        )}
      </Stack>
    );
  };

  const renderCodesTable = () => {
    const keys = selectedDeviceDetail?.keys || [];
    if (!keys.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminNoCodes")}
        </Typography>
      );
    }

    const total = keys.length;
    const page = clampPageIndex(codesPage, total);
    const start = page * ROWS_PER_PAGE;
    const pagedKeys = keys.slice(start, start + ROWS_PER_PAGE);

    return (
      <Stack spacing={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("passkey")}</TableCell>
              <TableCell>{t("adminAddedBy")}</TableCell>
              <TableCell>{t("validFrom")}</TableCell>
              <TableCell>{t("validTo")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedKeys.map((key) => (
              <TableRow key={key.id}>
                <TableCell>{key.passkey || t("unknown")}</TableCell>
                <TableCell>
                  {key.added_by_username || key.added_by || t("unknown")}
                </TableCell>
                <TableCell>
                  {formatDateTime(key.valid_from, t("unknown"))}
                </TableCell>
                <TableCell>
                  {formatDateTime(key.valid_to, t("unknown"))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {total > ROWS_PER_PAGE && (
          <TablePagination
            component="div"
            count={total}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            page={page}
            onPageChange={(_, newPage) => setCodesPage(newPage)}
          />
        )}
      </Stack>
    );
  };

  const renderHistoryTable = () => {
    const history = selectedDeviceDetail?.history || [];
    if (!history.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminNoHistory")}
        </Typography>
      );
    }

    const total = history.length;
    const page = clampPageIndex(historyPage, total);
    const start = page * ROWS_PER_PAGE;
    const pagedHistory = history.slice(start, start + ROWS_PER_PAGE);

    return (
      <Stack spacing={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("passkey")}</TableCell>
              <TableCell>{t("adminTimestamp")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedHistory.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{entry.key || t("unknown")}</TableCell>
                <TableCell>
                  {formatDateTime(entry.created_at, t("unknown"))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {total > ROWS_PER_PAGE && (
          <TablePagination
            component="div"
            count={total}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            page={page}
            onPageChange={(_, newPage) => setHistoryPage(newPage)}
          />
        )}
      </Stack>
    );
  };

  const renderFirmwareHistoryTable = () => {
    const history = selectedDeviceDetail?.firmware_history || [];
    if (!history.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminFirmwareNoHistory")}
        </Typography>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("adminFirmwareVersion")}</TableCell>
            <TableCell>{t("adminFirmwareRecordedAt")}</TableCell>
            <TableCell>{t("adminFirmwareRelease")}</TableCell>
            <TableCell>{t("adminFirmwareDownload")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell>{entry.version || t("unknown")}</TableCell>
              <TableCell>
                {formatDateTime(entry.recorded_at, t("unknown"))}
              </TableCell>
              <TableCell>
                {entry.release_version || t("adminFirmwareReleaseUnknown")}
              </TableCell>
              <TableCell>
                {entry.download_url ? (
                  <Button
                    size="small"
                    onClick={() =>
                      handleSecureDownload(
                        entry.download_url,
                        `${entry.version || "firmware"}.bin`
                      )
                    }
                  >
                    {t("adminFirmwareDownload")}
                  </Button>
                ) : (
                  "—"
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderFirmwareUpdateControls = () => {
    if (!selectedDeviceDetail) {
      return null;
    }

    const deviceTypeId = selectedDeviceDetail.device?.device_type_id || null;
    const isBlocked = Boolean(selectedDeviceDetail.device?.is_blocked);
    const matchingReleases = firmwareReleases.filter((release) => {
      if (!deviceTypeId) {
        return true;
      }
      return release.device_type_id === deviceTypeId;
    });
    const disableUpdate =
      isBlocked ||
      !selectedFirmwareReleaseId ||
      sendingFirmwareUpdate ||
      !matchingReleases.length;
    const currentTypeMatches = Boolean(deviceTypeId);

    return (
      <Stack spacing={1}>
        <Stack
          spacing={1}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            select
            fullWidth
            label={t("adminFirmwareSelectRelease")}
            value={selectedFirmwareReleaseId}
            onChange={(e) => setSelectedFirmwareReleaseId(e.target.value)}
            size="small"
            disabled={!matchingReleases.length}
          >
            <MenuItem value="">
              {matchingReleases.length
                ? t("adminFirmwareSelectReleasePlaceholder")
                : t("adminFirmwareNoReleasesAvailable")}
            </MenuItem>
            {matchingReleases.map((release) => (
              <MenuItem key={release.id} value={release.id}>
                {`${release.version} • ${release.device_type_name || t("unknown")}`}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={handleSendFirmwareUpdate}
            disabled={disableUpdate}
          >
            {t("adminFirmwareUpdateButton")}
          </Button>
        </Stack>
        {!currentTypeMatches && (
          <Typography variant="body2" color="warning.main">
            {t("adminFirmwareDeviceTypeRequiredForUpdate")}
          </Typography>
        )}
        {currentTypeMatches && !matchingReleases.length && (
          <Typography variant="body2" color="text.secondary">
            {t("adminFirmwareNoReleasesForType")}
          </Typography>
        )}
        {isBlocked && (
          <Typography variant="body2" color="error">
            {t("adminDeviceBlockedLabel")}
          </Typography>
        )}
        <Divider />
        <Stack spacing={1}>
          <Typography variant="subtitle2">
            {t("adminManufactureDeviceSpecificTitle")}
          </Typography>
          <Stack
            spacing={1}
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              fullWidth
              label={t("adminManufactureDevicePattern")}
              value={manufacturingPattern}
              onChange={(e) => setManufacturingPattern(e.target.value)}
              size="small"
              helperText={t("adminManufacturePatternHint")}
            />
            <Button
              variant="outlined"
              onClick={handleFetchManufacturingMatches}
              disabled={loadingManufacturingMatches}
            >
              {t("adminManufactureSearchButton")}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSendManufacturingUpdate}
              disabled={sendingManufacturingUpdate || !selectedDeviceId}
            >
              {t("adminManufactureSendButton")}
            </Button>
          </Stack>
          {loadingManufacturingMatches ? (
            <Typography variant="body2" color="text.secondary">
              {t("adminManufactureSearching")}
            </Typography>
          ) : manufacturingMatches.length ? (
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={600}>
                {t("adminManufactureMatchesTitle")}
              </Typography>
              {manufacturingMatches.map((match) => (
                <Stack
                  key={match.filename}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="body2">{match.filename}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(match.created_at, t("unknown"))} •{" "}
                      {match.size ? formatFileSize(match.size) : "—"}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleSecureDownload(match.download_url, match.filename)
                    }
                  >
                    {t("adminManufactureDownload")}
                  </Button>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("adminManufactureNoMatches")}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  const renderDeviceTypeControls = () => {
    if (loadingDeviceTypes) {
      return (
        <Box p={1}>
          <CircularProgress size={20} />
        </Box>
      );
    }

    const hasOptions = deviceTypes.length > 0;
    const currentTypeId = selectedDeviceDetail?.device?.device_type_id
      ? String(selectedDeviceDetail.device.device_type_id)
      : "";
    const disableUpdate =
      updatingDeviceType || deviceTypeDraft === currentTypeId;

    return (
      <Stack spacing={1}>
        <Stack
          spacing={1}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            select
            fullWidth
            size="small"
            label={t("adminDeviceType")}
            value={deviceTypeDraft}
            onChange={(e) => setDeviceTypeDraft(e.target.value)}
            disabled={!hasOptions}
          >
            <MenuItem value="">{t("adminDeviceTypeUnsetOption")}</MenuItem>
            {deviceTypes.map((type) => (
              <MenuItem key={type.id} value={String(type.id)}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={handleUpdateDeviceType}
            disabled={disableUpdate}
          >
            {t("adminDeviceTypeUpdateButton")}
          </Button>
        </Stack>
        {!hasOptions && (
          <Typography variant="body2" color="text.secondary">
            {t("adminDeviceTypeNoOptions")}
          </Typography>
        )}
      </Stack>
    );
  };

  const renderDeviceTypeList = () => {
    if (loadingDeviceTypes) {
      return (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!deviceTypes.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminDeviceTypeNoRecords")}
        </Typography>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>{t("adminDeviceType")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deviceTypes.map((type) => (
            <TableRow key={type.id}>
              <TableCell>{type.id}</TableCell>
              <TableCell>{type.name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderDeviceTypeCreateForm = () => (
    <Stack
      spacing={1}
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <TextField
        label={t("adminDeviceTypeName")}
        value={newDeviceTypeName}
        onChange={(e) => setNewDeviceTypeName(e.target.value)}
        size="small"
      />
      <Button
        variant="contained"
        onClick={handleCreateDeviceType}
        disabled={creatingDeviceType || !newDeviceTypeName.trim()}
      >
        {t("adminDeviceTypeCreateButton")}
      </Button>
    </Stack>
  );

  const renderFirmwareReleaseTable = () => {
    const filteredReleases = firmwareReleases.filter((release) => {
      if (!firmwareFilterType) {
        return true;
      }
      return String(release.device_type_id) === firmwareFilterType;
    });

    return (
      <Stack spacing={2}>
        <TextField
          select
          size="small"
          label={t("adminFirmwareFilterByType")}
          value={firmwareFilterType}
          onChange={(e) => setFirmwareFilterType(e.target.value)}
        >
          <MenuItem value="">{t("adminFirmwareFilterAll")}</MenuItem>
          {deviceTypes.map((type) => (
            <MenuItem key={type.id} value={String(type.id)}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>
        {loadingFirmwareReleases ? (
          <Box p={2} display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </Box>
        ) : filteredReleases.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("adminFirmwareVersion")}</TableCell>
                <TableCell>{t("adminDeviceType")}</TableCell>
              <TableCell>{t("adminFirmwareUploadedAt")}</TableCell>
              <TableCell>{t("adminFirmwareSize")}</TableCell>
              <TableCell>{t("adminFirmwareDownload")}</TableCell>
              <TableCell align="right">{t("adminActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReleases.map((release) => (
              <TableRow key={release.id}>
                  <TableCell>{release.version}</TableCell>
                  <TableCell>{release.device_type_name || t("unknown")}</TableCell>
                  <TableCell>
                    {formatDateTime(release.uploaded_at, t("unknown"))}
                  </TableCell>
                  <TableCell>
                    {typeof release.file_size === "number"
                      ? formatFileSize(release.file_size)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {release.download_url ? (
                      <Button
                        size="small"
                        onClick={() =>
                          handleSecureDownload(
                            release.download_url,
                            `${release.version}.bin`
                          )
                        }
                      >
                        {t("adminFirmwareDownload")}
                      </Button>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("adminFirmwareDelete")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteFirmwareRelease(release)}
                          disabled={
                            deletingFirmwareReleaseId === Number(release.id)
                          }
                        >
                          {deletingFirmwareReleaseId === Number(release.id) ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("adminFirmwareNoReleases")}
          </Typography>
        )}
      </Stack>
    );
  };

  const renderFirmwareUploadForm = () => {
    if (!deviceTypes.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminFirmwareNeedDeviceType")}
        </Typography>
      );
    }

    return (
      <Stack spacing={1}>
      <Stack
        spacing={1}
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <TextField
          select
          size="small"
          label={t("adminDeviceType")}
          value={firmwareUpload.deviceTypeId}
          onChange={(e) =>
            setFirmwareUpload((prev) => ({
              ...prev,
              deviceTypeId: e.target.value,
            }))
          }
        >
          {deviceTypes.map((type) => (
            <MenuItem key={type.id} value={String(type.id)}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label={t("adminFirmwareVersion")}
          value={firmwareUpload.version}
          onChange={(e) =>
            setFirmwareUpload((prev) => ({
              ...prev,
              version: e.target.value,
            }))
          }
        />
      </Stack>
      <TextField
        multiline
        minRows={2}
        size="small"
        label={t("adminFirmwareNotes")}
        value={firmwareUpload.notes}
        onChange={(e) =>
          setFirmwareUpload((prev) => ({
            ...prev,
            notes: e.target.value,
          }))
        }
      />
      <Stack
        spacing={1}
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Button variant="outlined" component="label">
          {firmwareFile
            ? firmwareFile.name
            : t("adminFirmwareSelectFileButton")}
          <input
            type="file"
            hidden
            accept=".bin,application/octet-stream"
            onChange={handleFirmwareFileChange}
          />
        </Button>
        <Button
          variant="contained"
          onClick={handleFirmwareUpload}
          disabled={uploadingFirmware}
        >
          {t("adminFirmwareUploadButton")}
        </Button>
      </Stack>
    </Stack>
  );
  };

  const renderWifiStatus = () => {
    const wifiStatus = selectedDeviceDetail?.status?.wifi_status;
    if (!wifiStatus) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminWifiStatusUnknown")}
        </Typography>
      );
    }

    return (
      <Stack spacing={0.5}>
        <Typography variant="body2">
          {t("adminWifiSsid")}:
          {" "}
          <strong>{wifiStatus.ssid || t("unknown")}</strong>
        </Typography>
        <Typography variant="body2">
          {t("adminWifiRssi")}:
          {" "}
          {typeof wifiStatus.rssi === "number"
            ? `${wifiStatus.rssi} dBm`
            : t("unknown")}
        </Typography>
        <Typography variant="body2">
          {t("adminWifiBssid")}:
          {" "}
          {wifiStatus.bssid || t("unknown")}
        </Typography>
        <Typography variant="body2">
          {t("adminWifiChannel")}:
          {" "}
          {wifiStatus.channel ?? t("unknown")}
        </Typography>
        <Typography variant="body2">
          {t("adminWifiIp")}:
          {" "}
          {wifiStatus.ip_address || t("unknown")}
        </Typography>
        <Typography variant="body2">
          {t("adminWifiMac")}:
          {" "}
          {wifiStatus.mac_address || t("unknown")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("adminWifiUpdatedAt")}:
          {" "}
          {formatDateTime(wifiStatus.updated_at, t("unknown"))}
        </Typography>
      </Stack>
    );
  };

  const renderManufacturingSection = () => (
    <Paper variant="outlined">
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="subtitle1">
          {t("adminManufactureTitle")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            fetchDevices();
            fetchDeviceTypes();
          }}
          disabled={manufacturingLoading}
        >
          {t("adminRefresh")}
        </Button>
      </Box>
      <Divider />
      <Box p={2}>
        <Stack spacing={2}>
          <Stack
            spacing={1}
            direction={{ xs: "column", sm: "row" }}
          >
            <TextField
              fullWidth
              label={t("adminManufactureDeviceName")}
              value={manufacturingForm.deviceName}
              onChange={handleManufacturingInputChange("deviceName")}
              size="small"
            />
            <TextField
              select
              fullWidth
              label={t("adminManufactureDeviceType")}
              value={manufacturingForm.deviceTypeId}
              onChange={handleManufacturingInputChange("deviceTypeId")}
              size="small"
            >
              <MenuItem value="">
                {t("adminDeviceTypeUnsetOption")}
              </MenuItem>
              {deviceTypes.map((type) => (
                <MenuItem key={type.id} value={String(type.id)}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack
            spacing={1}
            direction={{ xs: "column", sm: "row" }}
          >
            <TextField
              fullWidth
              label={t("adminManufactureSerial")}
              value={manufacturingForm.serial}
              onChange={handleManufacturingInputChange("serial")}
              size="small"
            />
            <TextField
              select
              fullWidth
              label={t("adminManufactureKeyLength")}
              value={manufacturingForm.keyLength}
              onChange={handleManufacturingInputChange("keyLength")}
              size="small"
            >
              <MenuItem value="4">4</MenuItem>
              <MenuItem value="6">6</MenuItem>
            </TextField>
          </Stack>
          <Stack
            spacing={1}
            direction={{ xs: "column", sm: "row" }}
          >
            <TextField
              fullWidth
              label={t("adminManufactureWifiSsid")}
              value={manufacturingForm.wifiSsid}
              onChange={handleManufacturingInputChange("wifiSsid")}
              size="small"
            />
            <TextField
              fullWidth
              label={t("adminManufactureWifiPass")}
              type="password"
              value={manufacturingForm.wifiPassword}
              onChange={handleManufacturingInputChange("wifiPassword")}
              size="small"
            />
          </Stack>
          <TextField
            label={t("adminManufactureEnvironment")}
            value={manufacturingForm.environment}
            onChange={handleManufacturingInputChange("environment")}
            size="small"
            helperText={t("adminManufactureEnvironmentHint")}
          />
          <Box>
            <Button
              variant="contained"
              onClick={handleManufactureDevice}
              disabled={manufacturingLoading}
            >
              {t("adminManufactureSubmit")}
            </Button>
          </Box>
          {manufacturingResult && (
            <Stack spacing={1}>
              <Divider />
              <Typography variant="subtitle2">
                {t("adminManufactureResultTitle")}
              </Typography>
              <Typography variant="body2">
                {t("adminManufactureDeviceId")}:{" "}
                {manufacturingResult.device_id ?? t("unknown")}
              </Typography>
              <Typography variant="body2">
                {t("adminManufactureCreatedLabel")}:{" "}
                {manufacturingResult.device_created
                  ? t("adminManufactureCreatedNew")
                  : t("adminManufactureCreatedExisting")}
              </Typography>
              <Typography variant="body2">
                {t("adminManufactureDeviceName")}:{" "}
                {manufacturingResult.device_name ||
                  manufacturingForm.deviceName ||
                  t("unknown")}
              </Typography>
              <Typography variant="body2">
                {t("adminCloudDeviceId")}:{" "}
                {manufacturingResult.serial_number || t("unknown")}
              </Typography>
              <Typography variant="body2">
                {t("adminManufactureToken")}:{" "}
                <Box
                  component="span"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {manufacturingResult.token || t("unknown")}
                </Box>
              </Typography>
              {manufacturingResult.download_url && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    handleSecureDownload(
                      manufacturingResult.download_url,
                      manufacturingResult.filename || undefined
                    )
                  }
                >
                  {t("adminManufactureDownload")}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </Paper>
  );

  const renderWifiConfigForm = () => {
    return (
      <Stack spacing={1}>
        <Stack
          spacing={1}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            size="small"
            label={t("adminWifiSsid")}
            value={wifiConfigDraft.ssid}
            onChange={handleWifiConfigFieldChange("ssid")}
          />
          <TextField
            size="small"
            label={t("adminWifiPassword")}
            value={wifiConfigDraft.password}
            onChange={handleWifiConfigFieldChange("password")}
          />
          <Button
            variant="contained"
            onClick={handleSaveWifiConfig}
            disabled={savingWifiConfig}
          >
            {t("adminWifiSaveConfig")}
          </Button>
        </Stack>
        {selectedDeviceDetail?.wifi_config?.updated_at && (
          <Typography variant="caption" color="text.secondary">
            {t("adminWifiConfigUpdatedAt", {
              value: formatDateTime(
                selectedDeviceDetail.wifi_config.updated_at,
                t("unknown")
              ),
            })}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {t("adminWifiConfigHint")}
        </Typography>
      </Stack>
    );
  };

  const renderCloudConfig = () => {
    return (
      <Stack spacing={1}>
        <Stack
          spacing={1}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            size="small"
            label={t("adminCloudDeviceId")}
            value={cloudConfigDraft.cloud_device_id}
            onChange={handleCloudConfigFieldChange("cloud_device_id")}
          />
          <TextField
            size="small"
            label={t("adminCloudToken")}
            type="password"
            value={cloudConfigDraft.cloud_token}
            onChange={handleCloudConfigFieldChange("cloud_token")}
            autoComplete="new-password"
          />
          <TextField
            select
            size="small"
            label={t("adminCloudKeyLength")}
            value={cloudConfigDraft.key_length}
            onChange={handleCloudConfigFieldChange("key_length")}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="4">4</MenuItem>
            <MenuItem value="6">6</MenuItem>
          </TextField>
          <Button
            variant="contained"
            onClick={handleSaveCloudConfig}
            disabled={savingCloudConfig}
          >
            {t("adminCloudSaveConfig")}
          </Button>
        </Stack>
        {selectedDeviceDetail?.cloud_config?.updated_at && (
          <Typography variant="caption" color="text.secondary">
            {t("adminCloudConfigUpdatedAt", {
              value: formatDateTime(
                selectedDeviceDetail.cloud_config.updated_at,
                t("unknown")
              ),
            })}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {t("adminCloudConfigHint")}
        </Typography>
      </Stack>
    );
  };

  const renderStateEventsTable = () => {
    const events = selectedDeviceDetail?.state_events || [];
    if (!events.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminStateEventsEmpty")}
        </Typography>
      );
    }

    const total = events.length;
    const page = clampPageIndex(eventsPage, total);
    const start = page * ROWS_PER_PAGE;
    const pagedEvents = events.slice(start, start + ROWS_PER_PAGE);

    return (
      <Stack spacing={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("adminStateEventsType")}</TableCell>
              <TableCell>{t("adminStateEventsStatus")}</TableCell>
              <TableCell>{t("adminStateEventsMessage")}</TableCell>
              <TableCell>{t("adminStateEventsTimestamp")}</TableCell>
              <TableCell>{t("adminStateEventsMetadata")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedEvents.map((event) => {
              let metadataString = "";
              if (event.metadata) {
                metadataString =
                  typeof event.metadata === "string"
                    ? event.metadata
                    : JSON.stringify(event.metadata);
              }

              return (
                <TableRow key={event.id}>
                  <TableCell>{event.event_type}</TableCell>
                  <TableCell>
                    {typeof event.success === "boolean"
                      ? event.success
                        ? t("adminStateEventsSuccess")
                        : t("adminStateEventsFailure")
                      : t("unknown")}
                  </TableCell>
                  <TableCell>{event.message || "—"}</TableCell>
                  <TableCell>
                    {formatDateTime(event.created_at, t("unknown"))}
                  </TableCell>
                  <TableCell>
                    {metadataString ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {metadataString}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {total > ROWS_PER_PAGE && (
          <TablePagination
            component="div"
            count={total}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            page={page}
            onPageChange={(_, newPage) => setEventsPage(newPage)}
          />
        )}
      </Stack>
    );
  };

  const renderAssignedUsers = () => {
    if (loadingUsers) {
      return (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!assignedUsers.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminNoAssignedUsers")}
        </Typography>
      );
    }

    return (
      <Stack spacing={1}>
        {assignedUsers.map((user) => (
          <Stack
            key={user.id}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {user.username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("adminUserRoleLabel", { role: user.role })}
              </Typography>
            </Box>
            <Tooltip title={t("adminUnassignUserTooltip")}>
              <IconButton
                color="error"
                onClick={() => handleUnlinkUser(user.id)}
                size="small"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
    );
  };

  const renderAssignUserForm = () => {
    return (
      <Stack spacing={2}>
        <Stack
          spacing={1}
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            select
            label={t("adminAssignUserSelect")}
            value={assignUserId}
            onChange={(e) => setAssignUserId(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
          >
            {availableUsers.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.username} ({user.email})
              </MenuItem>
            ))}
            {!availableUsers.length && (
              <MenuItem value="" disabled>
                {t("adminAssignUserNoOptions")}
              </MenuItem>
            )}
          </TextField>
          <Button
            variant="contained"
            onClick={handleLinkUser}
            disabled={!assignUserId || linkingUser}
          >
            {t("adminAssignUserButton")}
          </Button>
        </Stack>
        {ldapActive && (
          <Stack
            spacing={1}
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              label={t("adminAssignLdapIdentifier")}
              value={ldapAssignmentIdentifier}
              onChange={(e) => setLdapAssignmentIdentifier(e.target.value)}
              size="small"
              sx={{ minWidth: 280 }}
            />
            <Button
              variant="outlined"
              onClick={handleImportLdapForDevice}
              disabled={importingLdapUser}
            >
              {t("adminAssignLdapButton")}
            </Button>
          </Stack>
        )}
      </Stack>
    );
  };

  const renderUsersTable = () => {
    if (loadingUsers) {
      return (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    if (!users.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("adminNoUsers")}
        </Typography>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("adminUserName")}</TableCell>
            <TableCell>{t("adminUserEmail")}</TableCell>
            <TableCell>{t("adminUserRole")}</TableCell>
            <TableCell>{t("adminUserDevices")}</TableCell>
            {ldapActive && <TableCell>{t("adminUserType")}</TableCell>}
            <TableCell align="right">{t("adminActions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => {
            const userTypeLabel = user.is_ldap
              ? t("adminUserTypeLdap")
              : t("adminUserTypeLocal");
            return (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  {user.devices?.length
                    ? user.devices.map((device) => device.device_name).join(", ")
                    : t("adminUserNoDevices")}
                </TableCell>
                {ldapActive && (
                  <TableCell>
                    <Chip
                      size="small"
                      color={user.is_ldap ? "secondary" : "default"}
                      label={userTypeLabel}
                    />
                  </TableCell>
                )}
                <TableCell align="right">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenEditUser(user)}
                  >
                    {t("adminUserEditAction")}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const renderEditUserDialog = () => {
    if (!editingUser) {
      return null;
    }
    const userIsLdap = Boolean(editingUser.is_ldap);
    const dialogTitleName =
      editingUser.username || editingUser.email || t("unknown");
    return (
      <Dialog
        open
        onClose={handleCloseEditUser}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("adminUserEditTitle", { name: dialogTitleName })}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label={t("adminUserName")}
              value={editingUserDraft.username}
              onChange={(e) =>
                setEditingUserDraft((prev) => ({
                  ...prev,
                  username: e.target.value,
                }))
              }
              disabled={userIsLdap}
              fullWidth
            />
            <TextField
              label={t("adminUserEmail")}
              type="email"
              value={editingUserDraft.email}
              onChange={(e) =>
                setEditingUserDraft((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
              disabled={userIsLdap}
              fullWidth
            />
            {!userIsLdap && (
              <TextField
                label={t("adminUserEditPasswordLabel")}
                type="password"
                value={editingUserDraft.password}
                onChange={(e) =>
                  setEditingUserDraft((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                helperText={t("adminUserEditPasswordHint")}
                fullWidth
              />
            )}
            {userIsLdap && (
              <Typography variant="body2" color="text.secondary">
                {t("adminUserEditLdapWarning")}
              </Typography>
            )}
            <TextField
              select
              label={t("adminUserRole")}
              value={editingUserDraft.role}
              onChange={(e) =>
                setEditingUserDraft((prev) => ({
                  ...prev,
                  role: e.target.value,
                }))
              }
              fullWidth
            >
              <MenuItem value="user">{t("adminUserRoleUser")}</MenuItem>
              <MenuItem value="manager">{t("adminUserRoleManager")}</MenuItem>
              <MenuItem value="admin">{t("adminUserRoleAdmin")}</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditUser} disabled={savingUserEdit}>
            {t("adminUserEditCancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditedUser}
            disabled={savingUserEdit}
          >
            {t("adminUserEditSave")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderCreateUserForm = () => (
    <Stack spacing={1} direction={{ xs: "column", md: "row" }}>
      <TextField
        label={t("adminUserName")}
        value={newUser.username}
        onChange={(e) =>
          setNewUser((prev) => ({ ...prev, username: e.target.value }))
        }
        size="small"
      />
      <TextField
        label={t("adminUserEmail")}
        type="email"
        value={newUser.email}
        onChange={(e) =>
          setNewUser((prev) => ({ ...prev, email: e.target.value }))
        }
        size="small"
      />
      <TextField
        label={t("adminUserPassword")}
        type="password"
        value={newUser.password}
        onChange={(e) =>
          setNewUser((prev) => ({ ...prev, password: e.target.value }))
        }
        size="small"
      />
      <TextField
        select
        label={t("adminUserRole")}
        value={newUser.role}
        onChange={(e) =>
          setNewUser((prev) => ({ ...prev, role: e.target.value }))
        }
        size="small"
      >
        <MenuItem value="user">{t("adminUserRoleUser")}</MenuItem>
        <MenuItem value="manager">{t("adminUserRoleManager")}</MenuItem>
        <MenuItem value="admin">{t("adminUserRoleAdmin")}</MenuItem>
      </TextField>
      <Button
        variant="contained"
        onClick={handleCreateUser}
        disabled={creatingUser}
      >
        {t("adminCreateUserButton")}
      </Button>
    </Stack>
  );

  const renderDeviceOverviewTab = () => (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="subtitle1">{t("adminStatusTitle")}</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => selectedDeviceId && fetchDeviceDetail(selectedDeviceId)}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        {renderStatus()}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="subtitle1">
            {t("adminAssignedUsersTitle")}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (selectedDeviceId) {
                fetchDeviceDetail(selectedDeviceId);
              }
              fetchUsers();
            }}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        <Stack spacing={2}>
          {renderAssignedUsers()}
          {availableUsers.length ? (
            renderAssignUserForm()
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("adminAssignUserNoOptions")}
            </Typography>
          )}
        </Stack>
      </Paper>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box
              mb={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1">
                {t("adminDeviceTypeTitle")}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={fetchDeviceTypes}
                disabled={loadingDeviceTypes}
              >
                {t("adminRefresh")}
              </Button>
            </Box>
            {renderDeviceTypeControls()}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box
              mb={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1">
                {t("adminCloudConfigTitle")}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => selectedDeviceId && fetchDeviceDetail(selectedDeviceId)}
              >
                {t("adminRefresh")}
              </Button>
            </Box>
            {renderCloudConfig()}
          </Paper>
        </Grid>
      </Grid>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">{t("adminWifiTitle")}</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => selectedDeviceId && fetchDeviceDetail(selectedDeviceId)}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t("adminWifiStatusHeading")}
            </Typography>
            {renderWifiStatus()}
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t("adminWifiConfigHeading")}
            </Typography>
            {renderWifiConfigForm()}
          </Box>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">{t("adminFirmwareTitle")}</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              fetchFirmwareReleases();
              if (selectedDeviceId) {
                fetchDeviceDetail(selectedDeviceId);
              }
            }}
            disabled={loadingFirmwareReleases}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        <Stack spacing={2}>
          {renderFirmwareUpdateControls()}
          <Divider />
          {renderFirmwareHistoryTable()}
        </Stack>
      </Paper>
    </Stack>
  );

  const renderDeviceCodesTab = () => (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">{t("adminCodes")}</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => selectedDeviceId && fetchDeviceDetail(selectedDeviceId)}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        {renderCodesTable()}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">{t("adminHistory")}</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => selectedDeviceId && fetchDeviceDetail(selectedDeviceId)}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        {renderHistoryTable()}
      </Paper>
    </Stack>
  );

  const renderDeviceEventsTab = () => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="subtitle1">
          {t("adminStateEventsTitle")}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => selectedDeviceId && fetchDeviceDetail(selectedDeviceId)}
        >
          {t("adminRefresh")}
        </Button>
      </Box>
      {renderStateEventsTable()}
    </Paper>
  );

  const renderDeviceDetailPanel = () => {
    if (loadingDetail) {
      return (
        <Paper
          variant="outlined"
          sx={{ p: 4, display: "flex", justifyContent: "center" }}
        >
          <CircularProgress />
        </Paper>
      );
    }

    if (!selectedDeviceId) {
      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("adminSelectDevice")}
          </Typography>
        </Paper>
      );
    }

    if (!selectedDeviceDetail) {
      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("adminSelectDevice")}
          </Typography>
        </Paper>
      );
    }

    const deviceName =
      selectedDeviceDetail?.device?.device_name || t("unknown");
    const serialNumber = selectedDeviceDetail?.device?.serial_number;

    return (
      <Paper variant="outlined" sx={{ minHeight: 400 }}>
        <Box
          px={2}
          pt={2}
          pb={1}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={1}
        >
          <Box>
            <Typography variant="h6">{deviceName}</Typography>
            {serialNumber && (
              <Typography variant="body2" color="text.secondary">
                {serialNumber}
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => selectedDeviceId && fetchDeviceDetail(selectedDeviceId)}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        <Tabs
          value={deviceDetailTab}
          onChange={(_, value) => setDeviceDetailTab(value)}
          variant="scrollable"
          allowScrollButtons="auto"
          sx={{ px: 2, mt: 1 }}
        >
          <Tab value="overview" label={t("adminDeviceOverviewTab")} />
          <Tab value="codes" label={t("adminDeviceCodesTab")} />
          <Tab value="events" label={t("adminDeviceEventsTab")} />
        </Tabs>
        <Divider />
        <Box p={2}>
          {deviceDetailTab === "overview" && renderDeviceOverviewTab()}
          {deviceDetailTab === "codes" && renderDeviceCodesTab()}
          {deviceDetailTab === "events" && renderDeviceEventsTab()}
        </Box>
      </Paper>
    );
  };

  const renderDevicesSection = () => (
    <Grid
      container
      spacing={2}
      alignItems="stretch"
      sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}
    >
      <Grid item xs={12} md={4} display="flex">
        <Paper
          variant="outlined"
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            p={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1">
              {t("adminDevices")}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchDevices}
              disabled={loadingDevices}
            >
              {t("adminRefresh")}
            </Button>
          </Box>
          <Divider />
          <Box sx={{ flexGrow: 1, overflowY: "auto", maxHeight: 520 }}>
            {renderDeviceList()}
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12} md={8} display="flex">
        {renderDeviceDetailPanel()}
      </Grid>
    </Grid>
  );

  const renderFirmwareSection = () => (
    <Stack spacing={2}>
      {canManageSystem && (
        <Paper variant="outlined">
          <Box
            p={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1">
              {t("adminDeviceTypeManagementTitle")}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchDeviceTypes}
              disabled={loadingDeviceTypes}
            >
              {t("adminRefresh")}
            </Button>
          </Box>
          <Divider />
          <Box p={2}>
            {renderDeviceTypeList()}
            <Divider sx={{ my: 2 }} />
            {renderDeviceTypeCreateForm()}
          </Box>
        </Paper>
      )}
      <Paper variant="outlined">
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">
            {t("adminFirmwareManagementTitle")}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchFirmwareReleases}
            disabled={loadingFirmwareReleases}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        <Divider />
        <Box p={2}>
          {renderFirmwareReleaseTable()}
          {canManageSystem && (
            <>
              <Divider sx={{ my: 2 }} />
              {renderFirmwareUploadForm()}
            </>
          )}
        </Box>
      </Paper>
      {renderManufacturingSection()}
    </Stack>
  );

  const renderUsersSection = () =>
    !canManageSystem ? null : (
    <Stack spacing={2}>
      <Paper variant="outlined">
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1">
            {t("adminUserManagementTitle")}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchUsers}
            disabled={loadingUsers}
          >
            {t("adminRefresh")}
          </Button>
        </Box>
        <Divider />
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            {t("adminCreateUserHeading")}
          </Typography>
          {renderCreateUserForm()}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            {t("adminExistingUsersHeading")}
          </Typography>
          {renderUsersTable()}
        </Box>
      </Paper>
    </Stack>
  );

  const renderSystemSection = () => (
    <Paper variant="outlined">
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="subtitle1">
          {t("adminSystemSettingsTitle")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={fetchSystemSettings}
          disabled={loadingSystemSettings}
        >
          {t("adminRefresh")}
        </Button>
      </Box>
      <Divider />
      <Box p={2}>
        {loadingSystemSettings ? (
          <Box p={2} display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <TextField
              label={t("adminSystemBaseUrl")}
              value={systemSettings.app_base_url}
              onChange={(e) =>
                setSystemSettings((prev) => ({
                  ...prev,
                  app_base_url: e.target.value,
                }))
              }
              helperText={t("adminSystemBaseUrlHint")}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={systemSettings.require_email_verification}
                  onChange={(e) =>
                    setSystemSettings((prev) => ({
                      ...prev,
                      require_email_verification: e.target.checked,
                    }))
                  }
                />
              }
              label={t("adminSystemRequireEmailVerification")}
            />
            <Typography variant="caption" color="text.secondary">
              {systemSettings.smtp_configured
                ? t("adminSystemSmtpConfigured")
                : t("adminSystemSmtpMissing")}
            </Typography>
            <Divider />
            <Typography variant="subtitle2">
              {t("adminSystemLdapTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("adminSystemLdapInfo")}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={systemSettings.ldap.enabled}
                  onChange={(e) =>
                    handleLdapFieldChange("enabled", e.target.checked)
                  }
                />
              }
              label={t("adminSystemLdapEnabled")}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("adminSystemLdapUrl")}
                  value={systemSettings.ldap.url}
                  onChange={(e) => handleLdapFieldChange("url", e.target.value)}
                  placeholder="ldaps://ldap.example.com:636"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("adminSystemLdapBaseDn")}
                  value={systemSettings.ldap.base_dn}
                  onChange={(e) =>
                    handleLdapFieldChange("base_dn", e.target.value)
                  }
                  placeholder="dc=example,dc=com"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("adminSystemLdapBindDn")}
                  value={systemSettings.ldap.bind_dn}
                  onChange={(e) =>
                    handleLdapFieldChange("bind_dn", e.target.value)
                  }
                  placeholder="cn=service,ou=accounts,dc=example,dc=com"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("adminSystemLdapLoginAttribute")}
                  value={systemSettings.ldap.login_attribute}
                  onChange={(e) =>
                    handleLdapFieldChange("login_attribute", e.target.value)
                  }
                  helperText={t("adminSystemLdapLoginAttributeHint")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("adminSystemLdapEmailAttribute")}
                  value={systemSettings.ldap.email_attribute}
                  onChange={(e) =>
                    handleLdapFieldChange("email_attribute", e.target.value)
                  }
                  helperText={t("adminSystemLdapEmailAttributeHint")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("adminSystemLdapNameAttribute")}
                  value={systemSettings.ldap.name_attribute}
                  onChange={(e) =>
                    handleLdapFieldChange("name_attribute", e.target.value)
                  }
                  helperText={t("adminSystemLdapNameAttributeHint")}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label={t("adminSystemLdapBindPassword")}
              type="password"
              value={systemSettings.ldap.bind_password}
              onChange={(e) =>
                handleLdapFieldChange("bind_password", e.target.value)
              }
              helperText={
                systemSettings.ldap.clear_bind_password
                  ? t("adminSystemLdapBindPasswordCleared")
                  : t("adminSystemLdapBindPasswordHint")
              }
            />
            {(systemSettings.ldap.bind_password_set ||
              systemSettings.ldap.clear_bind_password) && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearLdapBindPassword}
                disabled={systemSettings.ldap.clear_bind_password}
              >
                {t("adminSystemLdapBindPasswordClear")}
              </Button>
            )}
            <TextField
              select
              label={t("adminSystemLdapDefaultRole")}
              value={systemSettings.ldap.default_role}
              onChange={(e) =>
                handleLdapFieldChange("default_role", e.target.value)
              }
            >
              <MenuItem value="user">{t("adminUserRoleUser")}</MenuItem>
              <MenuItem value="manager">{t("adminUserRoleManager")}</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={systemSettings.ldap.tls_insecure}
                  onChange={(e) =>
                    handleLdapFieldChange("tls_insecure", e.target.checked)
                  }
                />
              }
              label={t("adminSystemLdapTlsInsecure")}
            />
            <Typography variant="caption" color="text.secondary">
              {t("adminSystemLdapTlsInsecureHint")}
            </Typography>
            <Button
              variant="contained"
              onClick={handleSaveSystemSettings}
              disabled={savingSystemSettings}
            >
              {t("adminSystemSave")}
            </Button>
          </Stack>
        )}
      </Box>
    </Paper>
  );

  return (
    <>
      <Box mt={6} mb={6}>
        <Paper variant="outlined">
          <Box
            px={2}
            pt={2}
            pb={1}
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={1}
          >
            <Typography variant="h6">{t("adminPanelTitle")}</Typography>
          </Box>
          <Tabs
            value={mainTab}
            onChange={(_, value) => setMainTab(value)}
            variant="scrollable"
            allowScrollButtons="auto"
            sx={{ px: 2 }}
          >
            <Tab value="devices" label={t("adminTabDevices")} />
            {canManageDevices && (
              <Tab value="firmware" label={t("adminTabFirmware")} />
            )}
            {canManageSystem && (
              <Tab value="users" label={t("adminTabUsers")} />
            )}
            {canManageSystem && (
              <Tab value="system" label={t("adminSystemTab")} />
            )}
          </Tabs>
          <Divider />
          <Box p={2}>
            {mainTab === "devices" && renderDevicesSection()}
            {mainTab === "firmware" &&
              canManageDevices &&
              renderFirmwareSection()}
            {mainTab === "users" && canManageSystem && renderUsersSection()}
            {mainTab === "system" && canManageSystem && renderSystemSection()}
          </Box>
        </Paper>
      </Box>
      {renderEditUserDialog()}
    </>
  );
};

export default AdminDeviceDashboard;
